/**
 * The hazard kinds — closed-form SDFs, one shared node carve, intensity that never narrows a node.
 * This slice implements 9 of the Design Bible §9.2 twelve; the moving-node kinds (HelixBraid,
 * ChirpSweep, FeedbackLoop) are the documented next phase.
 *
 * Geometry: the lethal material fills the tunnel from the wall inward to `innerRadius(θ,t)`. The ship
 * is safe when it is inside that radius OR inside the guaranteed node arc. The inner edge IS the
 * rendered waveform silhouette (FS-06 R08: silhouette and SDF zero-set coincide) — "the waveform is
 * the level".
 */
import { PLAY_RADIUS, TUNNEL_RADIUS } from '../../core/constants';
import { angularDelta, clamp, dsin, lerp, smoothstep01, wrap2pi } from '../../core/math-det';
import type { HazardKind } from '../../core/event-map';
import {
  RING_THICKNESS,
  SLAB_INSET,
  bodyDistance,
  toTheta,
  withNode,
  withNodeAt,
  type HazardKindDef,
  type HazardParams,
} from './types';

const ECHO_COUNT = 5;
const ECHO_DECAY = 0.82;
const ECHO_RATE = 0.55;

const TAU = Math.PI * 2;

function fract(x: number): number {
  return x - Math.floor(x);
}
function roundEven(v: number): number {
  return 2 * Math.round(v / 2);
}

/** Deterministic value noise in [0,1] from a per-hazard seed (FS-06 R27 — never Math.random). */
function valueNoise(a: number, b: number, seed: number): number {
  const xi = Math.floor(a);
  const xf = a - xi;
  const h0 = hash2(xi, seed, b);
  const h1 = hash2(xi + 1, seed, b);
  const t = xf * xf * (3 - 2 * xf);
  return h0 + (h1 - h0) * t;
}
function hash2(i: number, seed: number, b: number): number {
  let h = Math.imul(i | 0, 0x27d4eb2d) ^ Math.imul(seed | 0, 0x165667b1) ^ Math.imul(Math.floor(b * 8) | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return ((h >>> 0) % 10000) / 10000;
}

// ── Ribbon-family bodies (inner edge = waveform) ────────────────────────────
function ribbonSDF(inner: number, x: number, y: number, p: HazardParams): number {
  const r = Math.hypot(x, y);
  const theta = toTheta(x, y);
  // One shape, two signs (FS-06 R36). `bodyDistance` mirrors the surface for axis polarity and
  // applies `reach`; the node carve below is identical in both polarities (R37).
  const dBody = bodyDistance(p, inner, r);
  return withNode(dBody, theta, p, r);
}

export const SineRibbon: HazardKindDef = {
  kind: 'SineRibbon',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.2,
  zFar: 0.2,
  telegraphLead: 1.1,
  silhouette: 'continuous-curve',
  nodeCount: 1,
  hue: 0.83,
  innerRadius(p, theta, t) {
    const shape = 0.5 * (1 + dsin(p.freq * theta + p.phase + p.rate * t));
    return TUNNEL_RADIUS - p.amp * shape;
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.4, 8.2, smoothstep01(i));
    p.freq = Math.round(lerp(2, 7, i));
    p.rate = lerp(0, 0.8, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const SawtoothComb: HazardKindDef = {
  kind: 'SawtoothComb',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.3,
  zFar: 0.3,
  telegraphLead: 1.1,
  silhouette: 'ramp-teeth',
  nodeCount: 1,
  hue: 0.09,
  innerRadius(p, theta, t) {
    const u = fract((theta + p.phase + p.rate * t) / (TAU / p.count));
    return TUNNEL_RADIUS - p.amp * u;
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.4, 7.6, smoothstep01(i));
    p.count = Math.round(lerp(3, 10, i));
    p.rate = lerp(0, 0.6, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const TriangleFence: HazardKindDef = {
  kind: 'TriangleFence',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.3,
  zFar: 0.3,
  telegraphLead: 1.0,
  silhouette: 'linear-peaks',
  nodeCount: 1,
  hue: 0.33,
  innerRadius(p, theta, t) {
    const u = fract((theta + p.phase + p.rate * t) / (TAU / p.count));
    const tri = 1 - Math.abs(2 * u - 1); // symmetric, [0,1]
    return TUNNEL_RADIUS - p.amp * tri;
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.6, 7.8, smoothstep01(i));
    p.count = Math.round(lerp(3, 11, i));
    p.rate = lerp(0, 0.5, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const StandingWave: HazardKindDef = {
  kind: 'StandingWave',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.25,
  zFar: 0.25,
  telegraphLead: 1.0,
  silhouette: 'standing-lobes',
  nodeCount: 3,
  hue: 0.55,
  innerRadius(p, theta, t) {
    const env = Math.abs(dsin(p.freq * theta + p.phase));
    // At the envelope's zeros, inner = TUNNEL_RADIUS regardless of t: the node never closes.
    return TUNNEL_RADIUS - p.amp * env * (0.5 + 0.5 * dsin(p.rate * t));
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.6, 8.2, smoothstep01(i));
    p.freq = Math.round(lerp(2, 6, i));
    p.rate = lerp(1.5, 5.0, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const SquareGate: HazardKindDef = {
  kind: 'SquareGate',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.4,
  zFar: 0.4,
  telegraphLead: 1.0,
  silhouette: 'hard-step',
  nodeCount: 1,
  hue: 0.0,
  innerRadius(p, theta, t) {
    const seg = Math.floor((theta + p.phase + p.rate * t) / (TAU / p.count));
    const high = (((seg % 2) + 2) % 2) === 0;
    return TUNNEL_RADIUS - (high ? p.amp : p.amp * 0.15);
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(3.0, 9.0, smoothstep01(i));
    p.count = roundEven(lerp(4, 12, i));
    p.rate = lerp(0, 0.5, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const BeatGate: HazardKindDef = {
  kind: 'BeatGate',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.5,
  zFar: 0.5,
  telegraphLead: 1.0,
  silhouette: 'radial-bars',
  nodeCount: 3,
  hue: 0.15,
  innerRadius(p, theta, t) {
    // Bars pulse with tLocal at the chart's tempo (rate = bpm/60, set at spawn). Never reads audio.
    const gateOpen = fract(p.phase + t * p.rate) > 0.5;
    const seg = Math.floor((theta + p.phase) / (TAU / p.count));
    const barClosed = !gateOpen && ((((seg % 2) + 2) % 2) === 0);
    return TUNNEL_RADIUS - (barClosed ? p.amp : 0);
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(3.0, 9.0, smoothstep01(i));
    p.count = roundEven(lerp(4, 10, i));
    // rate set to bpm/60 by the scheduler at spawn.
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

export const NoiseCloud: HazardKindDef = {
  kind: 'NoiseCloud',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.6,
  zFar: 0.6,
  telegraphLead: 1.4,
  silhouette: 'stochastic-field',
  nodeCount: 1,
  hue: 0.72,
  innerRadius(p, theta, t) {
    const n = valueNoise(theta * p.freq, t * p.rate, p.seed);
    return TUNNEL_RADIUS - p.amp * n;
  },
  signedDistance(p, x, y, t) {
    return ribbonSDF(this.innerRadius(p, toTheta(x, y), t), x, y, p);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.0, 6.4, smoothstep01(i));
    p.freq = lerp(6, 22, i);
    p.rate = lerp(0.2, 1.6, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

// ── Radial kind: node is the inner annulus (no angular carve) ───────────────
export const PulseRing: HazardKindDef = {
  kind: 'PulseRing',
  radialClass: 'rim',
  // R41 — radial by construction: the region they leave open IS the axis, so an 'axis' polarity
  // is meaningless and they never count as axis pressure.
  polarities: ['rim'],
  zNear: -0.25,
  zFar: 0.25,
  telegraphLead: 1.2,
  silhouette: 'concentric-ring',
  nodeCount: 1,
  hue: 0.5,
  innerRadius(p, _theta, t) {
    // The ring's radius (for rendering). Contracts toward `amp` as it approaches.
    return lerp(TUNNEL_RADIUS, p.amp, clamp(t * p.rate, 0, 1));
  },
  signedDistance(p, x, y, t) {
    const r = Math.hypot(x, y);
    const rRing = lerp(TUNNEL_RADIUS, p.amp, clamp(t * p.rate, 0, 1));
    // Safe inside the annulus; the ring wall is lethal.
    return Math.abs(r - rRing) - RING_THICKNESS;
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(6.0, 2.6, i); // decreases: higher intensity contracts further
    p.rate = lerp(0.35, 0.9, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * (p.amp - RING_THICKNESS);
  },
};

// ── Slab: fills the whole disc except the node ──────────────────────────────
export const SubBassSlab: HazardKindDef = {
  kind: 'SubBassSlab',
  radialClass: 'full',
  // Already covers the axis; polarity does not apply.
  polarities: ['rim'],
  zNear: -1.6,
  zFar: 1.6,
  telegraphLead: 1.2,
  silhouette: 'solid-slab',
  nodeCount: 1,
  hue: 0.95,
  innerRadius() {
    return 0.4; // renders as a near-full disc with the node gap
  },
  signedDistance(p, x, y, _t) {
    const r = Math.hypot(x, y);
    const theta = toTheta(x, y);
    const dBody = -(PLAY_RADIUS - r) - SLAB_INSET; // strongly negative across the disc
    return withNode(dBody, theta, p, r);
  },
  scaleByIntensity() {
    // Difficulty goes into depth (zFar-zNear) and node factor, handled at spawn — never node width.
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * PLAY_RADIUS;
  },
};

// ── Moving-node kinds ───────────────────────────────────────────────────────

/** Two counter-rotating helical ribbons; the safe node sweeps around the tunnel. */
export const HelixBraid: HazardKindDef = {
  kind: 'HelixBraid',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.45,
  zFar: 0.45,
  telegraphLead: 1.3,
  silhouette: 'twin-helix',
  nodeCount: 2,
  hue: 0.62,
  innerRadius(p, theta, t) {
    const th1 = p.phase + p.rate * t;
    const th2 = p.phase - p.rate * t;
    const s1 = 0.5 * (1 + dsin(2 * (theta - th1)));
    const s2 = 0.5 * (1 + dsin(2 * (theta - th2)));
    return TUNNEL_RADIUS - p.amp * Math.max(s1, s2);
  },
  nodeThetaAt(p, t) {
    return wrap2pi(p.nodeTheta + p.rate * t * 0.5);
  },
  signedDistance(p, x, y, t) {
    const r = Math.hypot(x, y);
    const theta = toTheta(x, y);
    const dBody = bodyDistance(p, this.innerRadius(p, theta, t), r);
    return withNodeAt(dBody, theta, this.nodeThetaAt!(p, t), p.nodeHalfTheta, r, p.nodeRMin, p.nodeRMax);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(2.8, 7.4, smoothstep01(i));
    p.freq = 2;
    p.rate = lerp(0.4, 1.2, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

/** A ribbon defined outward from a quadratically-sweeping safe arc — a true tracking dodge. */
export const ChirpSweep: HazardKindDef = {
  kind: 'ChirpSweep',
  radialClass: 'rim',
  polarities: ['rim', 'axis'],
  zNear: -0.35,
  zFar: 0.35,
  telegraphLead: 1.5,
  silhouette: 'converging-sweep',
  nodeCount: 1,
  hue: 0.42,
  innerRadius(p, theta, t) {
    const thn = p.nodeTheta + p.rate * t * t;
    const a = Math.abs(angularDelta(theta, thn));
    const s = smoothstep01((a - p.nodeHalfTheta) / (Math.PI - p.nodeHalfTheta));
    return TUNNEL_RADIUS - p.amp * s;
  },
  nodeThetaAt(p, t) {
    return wrap2pi(p.nodeTheta + p.rate * t * t);
  },
  signedDistance(p, x, y, t) {
    const r = Math.hypot(x, y);
    const theta = toTheta(x, y);
    const dBody = bodyDistance(p, this.innerRadius(p, theta, t), r);
    return withNodeAt(dBody, theta, this.nodeThetaAt!(p, t), p.nodeHalfTheta, r, p.nodeRMin, p.nodeRMax);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(4.0, 9.5, smoothstep01(i));
    p.rate = lerp(0.15, 0.6, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * (TUNNEL_RADIUS - p.amp);
  },
};

/** Nested self-similar rings echoing inward — one shared node clears the whole stack. */
export const FeedbackLoop: HazardKindDef = {
  kind: 'FeedbackLoop',
  radialClass: 'rim',
  // R41 — radial by construction: the region they leave open IS the axis, so an 'axis' polarity
  // is meaningless and they never count as axis pressure.
  polarities: ['rim'],
  zNear: -0.75,
  zFar: 0.75,
  telegraphLead: 1.3,
  silhouette: 'nested-echo',
  nodeCount: 1,
  hue: 0.88,
  innerRadius(p, _theta, t) {
    // Outermost currently-active echo radius, for rendering.
    let rOut = p.amp;
    for (let k = 0; k < ECHO_COUNT; k++) {
      const tk = t - k * p.rate;
      if (tk < 0) continue;
      const rk = lerp(TUNNEL_RADIUS, p.amp, clamp(tk * ECHO_RATE, 0, 1)) * Math.pow(ECHO_DECAY, k);
      rOut = rk;
      break;
    }
    return rOut;
  },
  signedDistance(p, x, y, t) {
    const r = Math.hypot(x, y);
    const theta = toTheta(x, y);
    let dBody = Infinity;
    for (let k = 0; k < ECHO_COUNT; k++) {
      const tk = t - k * p.rate;
      if (tk < 0) continue;
      const rk = lerp(TUNNEL_RADIUS, p.amp, clamp(tk * ECHO_RATE, 0, 1)) * Math.pow(ECHO_DECAY, k);
      const d = Math.abs(r - rk) - RING_THICKNESS * Math.pow(ECHO_DECAY, k);
      if (d < dBody) dBody = d;
    }
    if (!Number.isFinite(dBody)) dBody = r; // no echoes yet → interior safe
    return withNode(dBody, theta, p, r);
  },
  scaleByIntensity(i, p) {
    p.amp = lerp(5.0, 2.6, i); // innermost radius, decreases
    p.rate = lerp(0.3, 0.12, i);
  },
  nodeWidthAt(_i, p) {
    return 2 * p.nodeHalfTheta * TUNNEL_RADIUS;
  },
};

export const HAZARD_KINDS: Record<HazardKind, HazardKindDef> = {
  SineRibbon,
  SquareGate,
  SawtoothComb,
  TriangleFence,
  StandingWave,
  PulseRing,
  BeatGate,
  SubBassSlab,
  NoiseCloud,
  HelixBraid,
  ChirpSweep,
  FeedbackLoop,
};

export const ALL_KINDS: HazardKind[] = [
  'SineRibbon',
  'SawtoothComb',
  'TriangleFence',
  'StandingWave',
  'SquareGate',
  'BeatGate',
  'PulseRing',
  'SubBassSlab',
  'NoiseCloud',
  'HelixBraid',
  'ChirpSweep',
  'FeedbackLoop',
];
